import classNames from 'classnames';
import { use } from 'react';

import { WebsiteLink } from 'src/components/common/website-link';
import { AppLinksSection } from 'src/components/layout/footer/app-links-section';
import { BrandLogo } from 'src/components/layout/footer/brand-logo';
import { CopyrightInformation } from 'src/components/layout/footer/copyright-information';
import CountrySelection from 'src/components/layout/footer/country-selection';
import FooterCategory from 'src/components/layout/footer/footer-category';
import { SocialMediaSection } from 'src/components/layout/footer/social-media-section';
import { GlobalCmsContent } from 'src/content/global-content';
import { SiteContext } from 'src/context/site-context';
import { BuildType } from 'src/enums/build/build-type';
import {
  amplitudeBrandName,
  amplitudeLogoClickEvent,
  amplitudeTrackEvent,
} from 'src/helpers/amplitude-event-track';
import { matchReducedMotion } from 'src/styling/media-queries';

import * as styles from './index.module.scss';

const footerImageHeight = ['2.5rem', '2.25rem'];

const Footer = () => {
  const { footerContent } = use(GlobalCmsContent);
  const { buildType, pageLevel, isMeridian, language } = use(SiteContext);

  if (!footerContent) {
    return;
  }

  const numberOfFooterNavMenuSections = footerContent.footerNavMenuSections?.length ?? 0;

  const logoAmplitudeAction = () =>
    amplitudeTrackEvent(amplitudeLogoClickEvent, {
      custom_page: window.location.origin,
      custom_level: pageLevel,
      brand: amplitudeBrandName(buildType),
      buildType,
      language,
      ctaName: 'Logo',
      component: `Footer`,
    });

  const logoClick = () => {
    void logoAmplitudeAction();
    const prefersReducedMotion = matchReducedMotion().matches;
    document.body.scrollIntoView({ behavior: prefersReducedMotion ? 'instant' : 'smooth' });
  };

  const hasFooterSocialMediaLinks = !!footerContent.footerSocialMediaLinks.images?.length;
  const isTogetherFoundation = buildType === BuildType.TOGETHER_FOUNDATION;
  const hideCountrySelection = !isMeridian && !isTogetherFoundation;

  return (
    <footer className={styles.container} id="footer">
      <div className={styles.footer}>
        <div className={styles.topSectionWrapper}>
          <div className={classNames(styles.logo, styles.footerSection)}>
            <WebsiteLink to="/" goToGlobal handleClick={logoClick}>
              <BrandLogo buildType={buildType} />
            </WebsiteLink>
          </div>
          {numberOfFooterNavMenuSections > 1 &&
            new Array(numberOfFooterNavMenuSections - 1)
              .fill(null)
              .map((_, index) => (
                <div key={index} className={classNames(styles.footerSection, styles.desktopOnly)} />
              ))}
          {!!footerContent?.footerAppLinks?.images?.length && (
            <AppLinksSection
              title={footerContent.footerAppLinks.title}
              images={footerContent.footerAppLinks.images}
              footerImageHeight={footerImageHeight}
            />
          )}
        </div>
        <div className={styles.mainContentWrapper}>
          {footerContent.footerNavMenuSections.map(({ title, links }, index) => {
            const lastSection = index === footerContent.footerNavMenuSections.length - 1;
            return (
              <div className={styles.footerSection} key={title}>
                <FooterCategory title={title} links={links}>
                  {isTogetherFoundation && lastSection && (
                    <SocialMediaSection
                      title={null}
                      images={footerContent.footerSocialMediaLinks.images!}
                      footerImageHeight={footerImageHeight}
                    />
                  )}
                </FooterCategory>
              </div>
            );
          })}
          {hideCountrySelection && (
            <div className={styles.footerSection}>
              <div className={styles.sectionTitle}>
                {footerContent.footerCountrySelection.title}
              </div>
              <CountrySelection
                currentCountryText={footerContent.footerCountrySelection.currentCountryText}
                links={footerContent.footerCountrySelection.links ?? []}
              />
              {hasFooterSocialMediaLinks && (
                <SocialMediaSection
                  title={footerContent.footerSocialMediaLinks.title}
                  images={footerContent.footerSocialMediaLinks.images!}
                  footerImageHeight={footerImageHeight}
                />
              )}
            </div>
          )}
        </div>
        {isMeridian && hasFooterSocialMediaLinks && (
          <SocialMediaSection
            title={footerContent.footerSocialMediaLinks.title}
            images={footerContent.footerSocialMediaLinks.images!}
            footerImageHeight={footerImageHeight}
          />
        )}
        <CopyrightInformation
          copyrightInformation={footerContent.footerCopyrightInformation}
          imagesDisclaimer={footerContent.footerImagesDisclaimer}
        />
      </div>
    </footer>
  );
};

export default Footer;
